import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CourseHero, CourseOverview, RequirementsSection, CareerPathwaySection, CourseCTASection } from '../components/courses';
import { api } from '../services/api';

const PageContainer = styled.div`
  min-height: 100vh;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.primary};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  text-align: center;
  padding: 2rem;
`;

const ErrorTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['4xl']};
  color: ${props => props.theme.colors.error};
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.fontSizes.base};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: ${props => props.theme.transitions.base};
  
  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-2px);
  }
`;

const CourseDetailPage = () => {
  const { courseCode } = useParams();
  const [courseDetail, setCourseDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCourseDetail = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/course-details/${courseCode}`);
        setCourseDetail(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching course detail:', err);
        setError('Course not found or unavailable');
      } finally {
        setLoading(false);
      }
    };

    if (courseCode) {
      fetchCourseDetail();
    }
  }, [courseCode]);

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>Loading course details...</LoadingContainer>
      </PageContainer>
    );
  }

  if (error || !courseDetail) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>Course Not Found</ErrorTitle>
          <ErrorMessage>
            {error || 'The course you are looking for could not be found.'}
          </ErrorMessage>
          <BackButton onClick={() => window.history.back()}>
            Go Back
          </BackButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <CourseHero courseDetail={courseDetail} />
      <CourseOverview courseDetail={courseDetail} />
      <RequirementsSection courseDetail={courseDetail} />
      <CareerPathwaySection courseDetail={courseDetail} />
      <CourseCTASection courseDetail={courseDetail} />
    </PageContainer>
  );
};

export default CourseDetailPage; 